// One conflict, with every resolution the walk actually allows.
//
// Compromise comes first because most festival clashes are not either-or, and a
// tool that only ever offers a binary choice is worse than the group chat it
// replaces. Voting sits underneath it, and a priority token is the only way to say
// that this one matters more than the last three.

import { Link, useParams } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { resolveVote } from '../../engine/voting.ts';
import { fmtRange, fmtTime } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

export function ConflictDetail() {
  const { id } = useParams();
  const { state, actions, derived } = useApp();

  const entry = [...derived.conflictsByDay.entries()]
    .flatMap(([dayId, list]) => list.map((c) => ({ dayId, conflict: c })))
    .find((x) => x.conflict.id === id);

  if (!entry) {
    return (
      <div className="panel planning-page planning-empty">
        <h1>Choice unavailable</h1>
        <p className="muted">This choice is not in the current group suggestions. Review the latest list.</p>
        <Link to="/conflicts" className="buttonlink">View group choices</Link>
      </div>
    );
  }

  const { conflict } = entry;
  const day = state.days.find((d) => d.id === entry.dayId);
  const votes = state.votes.filter((v) => v.conflictId === conflict.id);
  const resolution = resolveVote({
    conflict,
    votes,
    ledger: state.ledger,
    settings: state.workspace?.settings ?? {
      hardLockCap: 3,
      tokenAllocation: 3,
      tokensForceOutcome: false,
      scoreVisibility: 'banded',
      groupObjective: 'ledger',
      voteWindowMinutes: 30,
    },
    cfg: state.config,
  });

  const ordered = [...conflict.options].sort((a, b) => {
    const rank = (k: string) => (k === 'compromise' ? 0 : k === 'split' ? 1 : 2);
    if (rank(a.kind) !== rank(b.kind)) return rank(a.kind) - rank(b.kind);
    return b.groupValue - a.groupValue;
  });

  return (
    <div className="planning-page conflict-detail-page" style={{ maxWidth: 820 }}>
      <Link className="buttonlink" to="/conflicts">Back to group choices</Link>
      <h1 style={{ marginTop: 8 }}>
        {day?.label} {fmtRange(conflict.windowStart, conflict.windowEnd)}
      </h1>
      <p className="lede">{conflict.recommendationReason}</p>

      <div className="panel">
        <h3>Where everyone stands</h3>
        <div className="rowlist">
          {conflict.memberPositions.map((p) => {
            const set = p.preferredSetId ? derived.setById.get(p.preferredSetId) : undefined;
            const artist = set ? derived.artistById.get(set.artistId) : undefined;
            const stage = set ? derived.stageById.get(set.stageId) : undefined;
            return (
              <div key={p.userId} className="row">
                <span>
                  {p.displayName} wants {artist?.name ?? 'to be elsewhere'}
                  <span className="sub" style={{ display: 'block' }}>
                    {stage?.name}
                    {set ? ` ${fmtRange(set.startTs, set.endTs)}` : ''}
                    {p.hasHardLock ? ' · hard lock' : ''}
                  </span>
                  {p.noteExcerpt && (
                    <span className="sub" style={{ display: 'block', color: 'var(--ink-dim)' }}>
                      "{p.noteExcerpt}"
                    </span>
                  )}
                </span>
                <span className={`position ${p.band}`}>
                  <span className="dot" />
                  {p.rawScore !== undefined ? p.rawScore : p.band}
                </span>
              </div>
            );
          })}
        </div>
        <p className="muted" style={{ fontSize: 16, marginBottom: 0 }}>
          {state.workspace?.settings.scoreVisibility === 'raw'
            ? 'This crew shows raw scores.'
            : 'Scores are shown as preference levels for this crew.'}
        </p>
      </div>

      <div className="panel">
        <h3>Compare options</h3>
        <div className="options">
          {ordered.map((option) => {
            const mine = votes.find((v) => v.userId === state.userId && v.optionId === option.id);
            return (
              <div
                key={option.id}
                className={[
                  'option',
                  option.id === conflict.recommendedOptionId ? 'recommended' : '',
                  option.feasible ? '' : 'infeasible',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span>
                  <span className="kind">{option.kind.replace(/_/g, ' ')}</span>
                  <br />
                  {option.label}
                  {option.segments.length > 0 && (
                    <span className="sub" style={{ display: 'block', color: 'var(--ink-faint)' }}>
                      {option.segments
                        .map((s) => {
                          const set = derived.setById.get(s.setId);
                          const artist = set ? derived.artistById.get(set.artistId) : undefined;
                          return `${artist?.name ?? s.setId} ${fmtTime(s.startTs)} to ${fmtTime(s.endTs)}`;
                        })
                        .join(', then ')}
                    </span>
                  )}
                  {!option.feasible && (
                    <span className="sub" style={{ display: 'block', color: 'var(--warn)' }}>
                      {option.infeasibleReason}
                    </span>
                  )}
                </span>
                <span className="flex planning-actions">
                  <button
                    className="small"
                    aria-label={`Vote for ${option.label}`}
                    aria-pressed={Boolean(mine && !mine.tokenSpent)}
                    disabled={!option.feasible}
                    onClick={() => void actions.castVote(conflict.id, option.id, false)}
                  >
                    {mine && !mine.tokenSpent ? 'Voted' : 'Vote'}
                  </button>
                  <button
                    className="small"
                    aria-label={`Use a priority token for ${option.label}`}
                    aria-pressed={Boolean(mine?.tokenSpent)}
                    disabled={!option.feasible || derived.tokensRemaining <= 0}
                    title={`${derived.tokensRemaining} priority tokens remaining`}
                    onClick={() => void actions.castVote(conflict.id, option.id, true)}
                  >
                    {mine?.tokenSpent ? 'Token used' : 'Use priority token'}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="panel">
        <h3>Where the vote stands</h3>
        <dl className="kv">
          <dt>Decision rule</dt>
          <dd>{resolution.rule.replace(/_/g, ' ')}</dd>
          <dt>Outcome</dt>
          <dd>{ordered.find((o) => o.id === resolution.optionId)?.label ?? 'No outcome yet'}</dd>
          <dt>Tokens you have left</dt>
          <dd>{derived.tokensRemaining}</dd>
        </dl>
        <p className="reason">{resolution.explanation}</p>
        <div className="rowlist" style={{ marginTop: 8 }}>
          {votes.map((v) => (
            <div key={v.userId} className="row">
              <span>
                {state.members.find((m) => m.userId === v.userId)?.displayName ?? v.userId}
                <span className="sub" style={{ display: 'block' }}>
                  {ordered.find((o) => o.id === v.optionId)?.label}
                  {v.tokenSpent ? ' · token spent' : ''}
                  {v.pending ? ' · queued until this phone has signal' : ''}
                </span>
              </span>
              <span className="muted" style={{ fontSize: 14 }}>
                {v.pending ? 'pending' : 'counted'}
              </span>
            </div>
          ))}
        </div>
        <button
          className="primary"
          style={{ marginTop: 10 }}
          disabled={!resolution.optionId}
          onClick={() => resolution.optionId && void actions.applyResolution(conflict.id, resolution.optionId)}
        >
          Apply this outcome
        </button>
      </div>
    </div>
  );
}
